/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { SimpleHeader, ProfileAvatar, Spinner, Button } from 'ui/components';
import translate from 'services/i18n/translate';
import { Ducks, User, Role, DomainUtils } from '@wit/vtv-x-foundation-lib';
import { PrivateRoutePath, StartUpRoutePath } from 'routes/paths';
import { addSettingsErrorEvent } from 'store/ducks/analytics/systemEvent';
import { ButtonType } from 'ui/components/Button';

const MAX_NAME_CHARS = 17;

export default function Profiles(): JSX.Element {
  const history = useHistory();
  const dispatch = useDispatch();

  // local state
  const [isDisabled] = useState(false);
  const [errorLoadingProfiles] = useState(false);
  const [profiles, setProfiles] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // global store state
  const isConverged = useSelector(Ducks.Household.Selectors.data.getIsConverged);
  const users: User[] = useSelector(Ducks.Household.Selectors.data.getUsers);
  const curUserId = useSelector(Ducks.Credentials.Selectors.getUserId);
  const maxProfiles = useSelector(Ducks.Household.Selectors.data.usersLimit);
  // switch user state
  const isProcessingSwitchUser = useSelector(Ducks.Credentials.Selectors.isCredentialsProcessing);
  const errorMessage = useSelector(Ducks.Credentials.Selectors.getError);

  const prevUserId = useRef<string | undefined>();

  useEffect(() => {
    if (errorMessage) {
      addSettingsErrorEvent(errorMessage);
    }
  }, [errorMessage]);

  useEffect(() => {
    const selectableUsers = DomainUtils.getSelectableUsers(isConverged, users);
    // Sort Users By Firstname and only if they have a role "normal"
    const sortedUsers = selectableUsers.sort((a: User, b: User) => {
      if (a.firstName && a.role === Role.NORMAL && b.firstName && b.role === Role.NORMAL) {
        return a.firstName.localeCompare(b.firstName);
      }
      return 0;
    });
    setProfiles(sortedUsers);
  }, [isConverged, users]);

  useEffect(() => {
    setIsLoading(isProcessingSwitchUser);
  }, [isProcessingSwitchUser]);

  useEffect(() => {
    // after the initial state from store
    if (prevUserId && prevUserId.current) {
      if (curUserId && curUserId !== prevUserId.current) {
        // new user profile selected
        history.replace(StartUpRoutePath.ROOT);
      }
    }

    prevUserId.current = curUserId;
  }, [curUserId, history]);

  const onProfileSelected = useCallback(
    (profile: User) => {
      dispatch(Ducks.Credentials.Creators.switchUser(profile.id, true));
      if (prevUserId.current === profile.id) {
        history.replace(StartUpRoutePath.ROOT);
      }
    },
    [dispatch, history]
  );

  const onNewProfileSelected = useCallback(
    (e) => {
      if (isLoading) {
        e.preventDefault();
      } else {
        history.push(PrivateRoutePath.PROFILES_ADD);
      }
    },
    [history, isLoading]
  );

  const handleSignOut = () => {
    dispatch(Ducks.Credentials.Creators.logout());
  };

  const handleRetry = () => {
    return null;
  };

  const renderProfileItem = useCallback(
    (profile: User) => {
      const name = profile.role === Role.DEFAULT ? translate['profiles.household.name'] : profile.firstName || '';
      return (
        <div className="profile-card" id={`Profile_selection___${profile.id}`}>
          <a
            id={`SelectProfile_profile___${profile.id}`}
            className={`highlight ${isDisabled ? 'disabled' : ''}`}
            onClick={() => {
              onProfileSelected(profile);
            }}
          >
            <div className="image">
              <ProfileAvatar profileId={profile.id} className="type" name={name} />
            </div>
            <p id={`Profile_name___${profile.id}`} className="label">
              {name.length > MAX_NAME_CHARS ? `${name.substring(0, MAX_NAME_CHARS)}...` : name}
            </p>
          </a>
        </div>
      );
    },
    [isDisabled, onProfileSelected]
  );

  const renderProfilesList = useMemo(() => {
    return profiles.map((profile: User) => {
      if (errorLoadingProfiles) {
        return null;
      }
      return (
        <div id={`Profile_id___${profile.id}`} className="centered-component" key={profile.id}>
          {renderProfileItem(profile)}
        </div>
      );
    });
  }, [errorLoadingProfiles, profiles, renderProfileItem]);

  const renderErrorMessage = (retryCallback: () => void) => {
    if (!errorMessage) {
      return null;
    }

    return (
      <>
        <span id="Profile_errorMessage">{errorMessage.translation}</span>
        <Button
          bemBlock="SelectProfile"
          bemModifier="retry"
          buttonType={ButtonType.BUTTON}
          text={translate['buttons.retry']}
          className="centered-button"
          onClick={retryCallback}
        />
      </>
    );
  };

  const renderAddProfileButton = useMemo(() => {
    return (
      <div className="centered-component">
        <div className="profile-card">
          <a
            id="SelectProfile_button___addProfile"
            className={isDisabled ? 'disabled' : ''}
            onClick={(e) => onNewProfileSelected(e)}
          >
            <div className="image">
              <span id="SelectProfile_buttonImage___addProfile" className="type newProfile" />
            </div>
            <div id="SelectProfile_buttonTitle___addProfile" className="label">
              {translate['profiles.addnewprofile']}
            </div>
          </a>
        </div>
      </div>
    );
  }, [isDisabled, onNewProfileSelected]);

  return (
    <div id="Profiles_list" className="template-holder back-nav authentication modal profiles">
      <SimpleHeader subtitle={translate['profiles.selectYourProfileTitle']} />
      <div id="Profiles_listContainer" className="component no-background">
        <Spinner isActive={isLoading} isEmbedded />

        {renderErrorMessage(handleRetry)}
        {renderProfilesList}
        {profiles.length < maxProfiles && !errorLoadingProfiles && renderAddProfileButton}
      </div>
      {!errorLoadingProfiles ? (
        <div className="profile-counter">
          <span id="Profiles_counter">{`${profiles.length} / ${maxProfiles} ${translate['profiles.profilesused']}`}</span>
          <Spinner isActive={isDisabled} isEmbedded />

          <Button
            bemBlock="SelectProfile"
            bemModifier="signOut"
            buttonType={ButtonType.BUTTON}
            className="centered-button"
            text={translate['menu.profile.signOut']}
            disabled={isDisabled}
            onClick={handleSignOut}
          />
        </div>
      ) : null}
    </div>
  );
}
